import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useDebounce } from '@gonfalon/async/useDebounce';
import type { SearchFieldProps as LPSearchFieldProps } from '@launchpad-ui/components';

import { Skeleton } from '../skeleton';

import { SearchField } from './SearchField';

export interface DebouncedSearchFieldProps extends LPSearchFieldProps {
  placeholder?: string | undefined;
  /**
   * Delay in milliseconds before onChange is called after user stops typing
   * @default 250
   */
  debounceDelay?: number;
  loading?: boolean;
}

export const DebouncedSearchField = forwardRef<{ clear: () => void }, DebouncedSearchFieldProps>(
  ({ value, onChange, onClear, debounceDelay = 250, loading = false, ...props }, ref) => {
    const [search, setSearch] = useState(value ?? '');
    const lastSentValueRef = useRef<string>(value ?? '');

    const handleDebouncedChange = useCallback(
      (nextValue: string) => {
        lastSentValueRef.current = nextValue;
        onChange?.(nextValue);
      },
      [onChange],
    );

    const debouncedOnChange = useDebounce(handleDebouncedChange, debounceDelay);

    useEffect(() => {
      if (value !== lastSentValueRef.current) {
        setSearch(value ?? '');
        lastSentValueRef.current = value ?? '';
      }
    }, [value]);

    useImperativeHandle(ref, () => ({
      clear: () => {
        debouncedOnChange.cancel();
        setSearch('');
        lastSentValueRef.current = '';
        onClear?.();
      },
    }));

    const handleChange = (nextValue: string) => {
      setSearch(nextValue);
      debouncedOnChange(nextValue);
    };

    const handleClear = () => {
      debouncedOnChange.cancel();
      setSearch('');
      lastSentValueRef.current = '';
      onClear?.();
    };

    if (loading) {
      return <Skeleton height="32px" width="255px" />;
    }

    return <SearchField {...props} value={search ?? ''} onChange={handleChange} onClear={handleClear} />;
  },
);

DebouncedSearchField.displayName = 'DebouncedSearchField';
